import { Button, type ButtonProps } from "antd";
import {
  memo,
  useEffect,
  useState,
  type CSSProperties,
  type FocusEvent,
  type MouseEvent,
  type ReactNode,
} from "react";
import { HEIGHT_MAP, VARIANT_STYLES } from "./constants";
import "./CustomButton.css";

type VariantState = "normal" | "hover" | "focus" | "disabled";
type FleetButtonVariant = keyof typeof VARIANT_STYLES;

export type CustomButtonSize = keyof typeof HEIGHT_MAP;
export type CustomButtonVariant = FleetButtonVariant | ButtonProps["type"];

export interface CustomButtonProps
  extends Omit<ButtonProps, "type" | "children" | "danger"> {
  label?: ReactNode;
  buttonSize?: CustomButtonSize;
  type?: CustomButtonVariant;
  tone?: string;
  danger?: boolean;
  style?: CSSProperties;
}

function CustomButton({
  label = "",
  buttonSize = "medium",
  type = "primary",
  tone = "default",
  style: customStyle = {},
  onMouseEnter,
  onMouseLeave,
  onFocus,
  onBlur,
  danger = false,
  className,
  ...buttonProps
}: CustomButtonProps) {
  const height = HEIGHT_MAP[buttonSize] ?? HEIGHT_MAP.medium;
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const isCustomVariant = Object.prototype.hasOwnProperty.call(
    VARIANT_STYLES,
    type as string,
  );
  const availableToneStyles = isCustomVariant
    ? VARIANT_STYLES[type as FleetButtonVariant]
    : undefined;
  const hasDangerTone = !!availableToneStyles?.danger;
  const requestedTone = danger && hasDangerTone ? "danger" : tone;
  const isDisabled = !!buttonProps.disabled;
  const variantState: VariantState = isDisabled
    ? "disabled"
    : isFocused
      ? "focus"
      : isHovered
        ? "hover"
        : "normal";
  const toneStyles = availableToneStyles
    ? (availableToneStyles[requestedTone as keyof typeof availableToneStyles] ??
      availableToneStyles.default)
    : undefined;

  const variantStyle = toneStyles
    ? {
        ...toneStyles.normal,
        ...toneStyles[variantState],
      }
    : {};
  const buttonStyle: CSSProperties = {
    height,
    transition:
      "background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease",
    borderRadius: "4px",
    fontWeight:
      type === "icon" ? undefined : isDisabled ? 400 : 600,
    ...variantStyle,
    ...customStyle,
  };

  useEffect(() => {
    if (isDisabled) {
      setIsHovered(false);
      setIsFocused(false);
    }
  }, [isDisabled]);

  const iconSizeClass =
    type === "icon" ? `custom-button-icon-${buttonSize}` : "";

  const linkNoMotionClass = type === "link" ? "custom-btn-link-check" : "";
  const mergedClassName = [
    iconSizeClass,
    className,
    linkNoMotionClass,
    `custom-variant-state-${variantState}`,
  ]
    .filter(Boolean)
    .join(" ");

  const handleMouseEnter = (event: MouseEvent<HTMLElement>) => {
    if (isCustomVariant && !isDisabled) {
      setIsHovered(true);
    }
    onMouseEnter?.(event);
  };

  const handleMouseLeave = (event: MouseEvent<HTMLElement>) => {
    if (isCustomVariant) {
      setIsHovered(false);
    }
    onMouseLeave?.(event);
  };

  const handleFocus = (event: FocusEvent<HTMLElement>) => {
    if (isCustomVariant && !isDisabled) {
      setIsFocused(true);
    }
    onFocus?.(event);
  };

  const handleBlur = (event: FocusEvent<HTMLElement>) => {
    if (isCustomVariant) {
      setIsFocused(false);
    }
    onBlur?.(event);
  };

  const antButtonType: ButtonProps["type"] = isCustomVariant
    ? "default"
    : (type as ButtonProps["type"]);

  return (
    <Button
      {...buttonProps}
      type={antButtonType}
      danger={!isCustomVariant && danger}
      style={buttonStyle}
      className={mergedClassName || undefined}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      {label}
    </Button>
  );
}

export default memo(CustomButton);
